import { useEffect } from "react";
import { importAiProject, useEditorStore } from "../store/editorStore.ts";
import { ProjectFileButton } from "./ProjectFileButton.tsx";

function formatReleaseDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(date);
}

export function VersionSetupScreen() {
  const availableVersions = useEditorStore(
    (state) => state.availableVersions
  );
  const latestVersionId = useEditorStore(
    (state) => state.latestVersionId
  );
  const selectedVersionId = useEditorStore(
    (state) => state.selectedVersionId
  );
  const versionStatus = useEditorStore(
    (state) => state.versionStatus
  );
  const versionError = useEditorStore(
    (state) => state.versionError
  );
  const loadVersionOptions = useEditorStore(
    (state) => state.loadVersionOptions
  );
  const setSelectedVersion = useEditorStore(
    (state) => state.setSelectedVersion
  );
  const startVersionedProject = useEditorStore(
    (state) => state.startVersionedProject
  );

  useEffect(() => {
    void loadVersionOptions();
  }, [loadVersionOptions]);

  const selectedVersion = availableVersions.find(
    (version) => version.id === selectedVersionId
  );
  const loading =
    versionStatus === "loading-list" ||
    versionStatus === "loading-project";

  return (
    <main className="version-setup-shell">
      <section className="version-setup-card">
        <div className="version-setup-kicker">Minecraft Java Edition</div>
        <h1>Choose the target version first</h1>
        <p>
          Voxel Studio locks each project to one Java Edition release.
          Block availability, blockstates, models, and textures are all
          loaded from that exact version&apos;s official client JAR.
        </p>

        <div className="version-feature-grid">
          <article>
            <strong>Version-gated blocks</strong>
            <span>
              Blocks that do not exist in the selected release are not
              offered in the palette.
            </span>
          </article>
          <article>
            <strong>Version-specific assets</strong>
            <span>
              Models and PNG textures are read from the same selected
              release instead of a shared global texture set.
            </span>
          </article>
          <article>
            <strong>Latest by default</strong>
            <span>
              The current Mojang latest release is preselected, but older
              modern Java releases remain available.
            </span>
          </article>
        </div>

        <label className="version-select-field">
          <span>Minecraft Java version</span>
          <select
            value={selectedVersionId}
            disabled={loading || availableVersions.length === 0}
            onChange={(event) =>
              setSelectedVersion(event.target.value)
            }
          >
            {availableVersions.map((version) => (
              <option key={version.id} value={version.id}>
                {version.id}
                {version.id === latestVersionId ? " — Latest" : ""}
                {" · "}
                {formatReleaseDate(version.releaseTime)}
              </option>
            ))}
          </select>
        </label>

        {selectedVersion && (
          <div className="selected-version-summary">
            <div>
              <span>Selected release</span>
              <strong>{selectedVersion.id}</strong>
            </div>
            <div>
              <span>Released</span>
              <strong>
                {formatReleaseDate(selectedVersion.releaseTime)}
              </strong>
            </div>
            <div>
              <span>Asset source</span>
              <strong>Official Mojang client JAR</strong>
            </div>
          </div>
        )}

        {versionError && (
          <div className="version-error" role="alert">
            {versionError}
          </div>
        )}

        <button
          className="version-start-button"
          type="button"
          disabled={loading || !selectedVersionId}
          onClick={() => void startVersionedProject()}
        >
          {versionStatus === "loading-project"
            ? "Loading version assets…"
            : versionStatus === "loading-list"
              ? "Loading release list…"
              : "Create version-locked project"}
        </button>

        <p className="version-setup-footnote">
          Changing version later starts a new project so blocks from
          incompatible releases cannot silently remain in the world.
        </p>
        <div className="version-project-actions">
          <ProjectFileButton />
          <button type="button" className="header-button" disabled={loading} onClick={()=>void importAiProject({
            format:"minecraft-world-designer",version:4,minecraftVersion:selectedVersionId || "1.21.8",blocks:[]
          },false)}>Create offline draft</button>
        </div>
        <p className="version-setup-footnote">Open AI project JSON or work offline with the curated palette. Offline previews use approximate cubes; target-version availability and vanilla geometry need official assets.</p>
      </section>
    </main>
  );
}
